import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Headphones, Video as VideoIcon, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";

const INITIAL = {
  title: "", description: "", audio_url: "", video_url: "",
  media_type: "audio", cover_image: "", duration: "", episode_number: 1, published: true,
};

export default function AdminPodcasts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/podcasts"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(INITIAL); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...INITIAL, ...it, media_type: it.media_type || (it.audio_url ? "audio" : "audio") });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // clear the irrelevant media field based on media_type
      if (payload.media_type === "audio") payload.video_url = "";
      else payload.audio_url = "";
      if (editing) await api.put(`/admin/podcasts/${editing.id}`, payload);
      else await api.post("/admin/podcasts", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/admin/podcasts/${it.id}`);
    toast.success("Deleted");
    load();
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const mediaTypes = [
    { id: "audio", label: "Audio File", Icon: Headphones, hint: "Upload .mp3 / .m4a / .wav" },
    { id: "video_file", label: "Video File", Icon: VideoIcon, hint: "Upload .mp4 / .webm" },
    { id: "video_youtube", label: "YouTube Link", Icon: Youtube, hint: "Paste a YouTube URL" },
  ];

  return (
    <div data-testid="admin-podcasts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading-xl text-3xl">PODCASTS</h1>
        <button onClick={startCreate} className="btn-primary text-sm" data-testid="admin-podcasts-new">
          <Plus className="w-4 h-4" /> New Episode
        </button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Ep #", "Title", "Type", "Duration", "Status"].map((c) => (
                <th key={c} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest">{c}</th>
              ))}
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              : items.length === 0 ? <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No episodes yet. Click "New Episode".</td></tr>
              : items.map((p) => {
                const t = p.media_type || (p.audio_url ? "audio" : "audio");
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`podcast-row-${p.id}`}>
                    <td className="px-4 py-3 font-semibold">{p.episode_number}</td>
                    <td className="px-4 py-3">{p.title}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${t === "audio" ? "bg-[#FFCC00]" : "bg-[#E65C00] text-white"}`}>
                        {t === "audio" ? "AUDIO" : t === "video_file" ? "VIDEO FILE" : "YOUTUBE"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{p.duration || "—"}</td>
                    <td className="px-4 py-3">{p.published ? "Published" : "Draft"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">{editing ? "Edit" : "New"} Episode</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-[120px_1fr] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Ep #</label>
                <input type="number" className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.episode_number} onChange={(e) => setField("episode_number", Number(e.target.value) || 1)} data-testid="podcast-ep-number" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Title</label>
                <input className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="podcast-title" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Description</label>
              <textarea rows={3} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="podcast-description" />
            </div>

            {/* Media type picker */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Media Type</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {mediaTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setField("media_type", t.id)}
                    className={`p-3 border-2 text-left transition-colors ${form.media_type === t.id ? "border-[#E65C00] bg-[#E65C00]/5" : "border-gray-200 hover:border-gray-400"}`}
                    data-testid={`podcast-mediatype-${t.id}`}
                  >
                    <t.Icon className={`w-5 h-5 ${form.media_type === t.id ? "text-[#E65C00]" : "text-gray-500"}`} />
                    <div className="font-heading font-bold uppercase text-xs mt-2">{t.label}</div>
                    <div className="text-[10px] text-gray-500 mt-1">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional media source */}
            {form.media_type === "audio" && (
              <FileUpload
                value={form.audio_url}
                onChange={(v) => setField("audio_url", v)}
                label="Audio File"
                accept="audio/*,.mp3,.m4a,.wav,.ogg"
                kind="audio"
              />
            )}
            {form.media_type === "video_file" && (
              <FileUpload
                value={form.video_url}
                onChange={(v) => setField("video_url", v)}
                label="Video File"
                accept="video/*,.mp4,.mov,.webm"
                kind="video"
              />
            )}
            {form.media_type === "video_youtube" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">YouTube URL</label>
                <input
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
                  value={form.video_url}
                  onChange={(e) => setField("video_url", e.target.value)}
                  data-testid="podcast-youtube-url"
                />
                <div className="text-[10px] text-gray-500 mt-1">Paste any YouTube watch / share / embed URL. The frontend player will embed it.</div>
              </div>
            )}

            <FileUpload
              value={form.cover_image}
              onChange={(v) => setField("cover_image", v)}
              label="Cover Image"
              accept="image/*"
              kind="image"
            />

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Duration (e.g. 32:15)</label>
              <input className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.duration} onChange={(e) => setField("duration", e.target.value)} data-testid="podcast-duration" />
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.published} onChange={(e) => setField("published", e.target.checked)} data-testid="podcast-published" />
              <span className="text-sm font-bold">Published</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="podcast-save"><Save className="w-4 h-4" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
